import { Timestamp } from 'firebase/firestore';

/** Stable color keys used across Board / Classes / Portfolios */
export type ClassroomTeamColorKey =
  | 'shark'
  | 'crew'
  | 'pixel'
  | 'reef'
  | 'ember'
  | 'slate';

export const CLASSROOM_TEAM_COLORS: Record<
  ClassroomTeamColorKey,
  { accent: string; soft: string; label: string }
> = {
  shark: { accent: '#0E8A6D', soft: '#E2F4EE', label: 'Shark' },
  crew: { accent: '#E0592E', soft: '#FBEDE6', label: 'Crew' },
  pixel: { accent: '#6C4FD8', soft: '#EFEBFB', label: 'Pixel' },
  reef: { accent: '#4A90E2', soft: '#EAF0FE', label: 'Reef' },
  ember: { accent: '#F5A623', soft: '#FDF3DC', label: 'Ember' },
  slate: { accent: '#2C3E50', soft: '#EEF1F4', label: 'Slate' },
};

export const CLASSROOM_TEAM_COLOR_ORDER: ClassroomTeamColorKey[] = [
  'shark',
  'crew',
  'pixel',
  'reef',
  'ember',
  'slate',
];

export interface ClassroomTeam {
  id: string;
  squadId: string;
  name: string;
  colorKey: ClassroomTeamColorKey;
  memberIds: string[];
  archived: boolean;
  createdAt: Timestamp | number;
  updatedAt: Timestamp | number;
  createdBy?: string;
  avatarUrl?: string;
  avatarKind?: 'muse' | 'upload';
}

export type PortfolioAiStatus = 'pending' | 'approved' | 'revealed' | 'skipped';

export type PortfolioAiCreationType =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'game_link'
  | 'claude_artifact'
  | 'model_3d'
  | 'link';

export const PORTFOLIO_AI_CREATION_TYPES: PortfolioAiCreationType[] = [
  'image',
  'video',
  'audio',
  'pdf',
  'game_link',
  'claude_artifact',
  'model_3d',
  'link',
];

export interface PortfolioPhoto {
  id: string;
  url: string;
  storagePath: string;
  uploadedAt: number;
  uploadedBy: string;
  attributedStudentIds?: string[];
  archived?: boolean;
  archivedAt?: number;
}

export function visiblePortfolioPhotos(photos?: PortfolioPhoto[]): PortfolioPhoto[] {
  return (photos || []).filter((photo) => !photo.archived);
}

export interface PortfolioAiGroup {
  id: string;
  title: string;
  kind: 'options' | 'shot';
  order: number;
  favoriteId?: string | null;
  notes?: string;
  reviewed?: boolean;
  updatedAt?: number;
  updatedBy?: string;
  /** Read-time provenance for legacy coalesced entries. */
  originEntryId?: string;
}

export interface PortfolioAiVersion {
  id: string;
  url?: string;
  storagePath?: string;
  status: PortfolioAiStatus;
  createdAt: number;
  updatedAt?: number;
  type?: PortfolioAiCreationType;
  posterUrl?: string;
  posterStoragePath?: string;
  filename?: string;
  caption?: string;
  sourcePhotoIds?: string[];
  groupId?: string;
  optionOrder?: number;
  originEntryId?: string;
  uploadedBy?: string;
  uploadedByName?: string;
  uploadedAt?: number;
  archived?: boolean;
  archivedAt?: number;
}

export type PortfolioEntryStatus =
  | 'draft'
  | 'has_photos'
  | 'ai_pending'
  | 'ai_ready'
  | 'revealed';

export interface PortfolioEntry {
  id: string;
  squadId: string;
  teamId: string;
  /**
   * Durable class-work key. New entries use `plan:{date|index}:{levelId}` so
   * uploads made during class and uploads added later resolve to one entry.
   * Older entries may still contain a liveSession.sessionId.
   */
  sessionId: string;
  levelId: string;
  levelTitle: string;
  slotIndex?: number;
  date?: string;
  memberIdsSnapshot: string[];
  photos: PortfolioPhoto[];
  aiVersions: PortfolioAiVersion[];
  aiGroups?: PortfolioAiGroup[];
  aiGroupsRevision?: number;
  teacherNote?: string | null;
  guess_text?: string | null;
  revealed_at?: number | null;
  status: PortfolioEntryStatus;
  createdAt: number;
  updatedAt: number;
}

/** Context used when creating/uploading portfolio work for the current pager slot */
export interface PortfolioSessionContext {
  sessionId: string;
  levelId: string;
  levelTitle: string;
  slotIndex?: number;
  date?: string;
}

export function buildPlanSessionId(args: {
  levelId: string;
  date?: string;
  slotIndex?: number;
  liveSessionId?: string | null;
}): string {
  const dateOrIndex = args.date || `i${args.slotIndex ?? 0}`;
  return `plan:${dateOrIndex}:${args.levelId}`;
}

/** Stable semantic identity used to recognize legacy live-session entries. */
export function portfolioClassKey(
  entry: Pick<PortfolioEntry, 'teamId' | 'levelId' | 'slotIndex' | 'date'>
): string {
  const day = entry.date || `i${entry.slotIndex ?? 0}`;
  return `${entry.teamId}::${day}::${entry.levelId}`;
}

export function portfolioEntryMatchesSlot(
  entry: Pick<PortfolioEntry, 'levelId' | 'slotIndex' | 'date'>,
  slot: { levelId: string; index: number; date?: string }
): boolean {
  if (typeof entry.slotIndex === 'number') return entry.slotIndex === slot.index;
  if (entry.date && slot.date) return entry.date === slot.date && entry.levelId === slot.levelId;
  return entry.levelId === slot.levelId;
}

export function recommendedRevealPlanIndex(
  entries: PortfolioEntry[],
  slots: Array<{ levelId: string; index: number; date?: string }>,
  teachingPlanIndex: number
): number {
  const grouped = slots.map((slot) =>
    coalescePortfolioEntries(entries.filter((entry) => portfolioEntryMatchesSlot(entry, slot)))
  );
  for (let index = teachingPlanIndex - 1; index >= 0; index -= 1) {
    if (grouped[index]?.some((entry) => portfolioRevealReadiness(entry) === 'ready_to_reveal')) return index;
  }
  for (let index = teachingPlanIndex - 1; index >= 0; index -= 1) {
    if (grouped[index]?.some((entry) => visiblePortfolioPhotos(entry.photos).length)) return index;
  }
  return teachingPlanIndex;
}

/** Present legacy duplicate documents as one lossless class card. */
export function coalescePortfolioEntries(entries: PortfolioEntry[]): PortfolioEntry[] {
  const groups = new Map<string, PortfolioEntry[]>();
  entries.forEach((entry) => {
    const key = portfolioClassKey(entry);
    groups.set(key, [...(groups.get(key) || []), entry]);
  });

  return Array.from(groups.values()).map((group) => {
    if (group.length === 1) return group[0];
    const ranked = [...group].sort((a, b) => {
      const contentA = (a.photos?.length || 0) + (a.aiVersions?.length || 0);
      const contentB = (b.photos?.length || 0) + (b.aiVersions?.length || 0);
      return contentB - contentA || a.createdAt - b.createdAt;
    });
    const primary = ranked[0];
    const unique = <T extends { id: string }>(items: T[]) =>
      Array.from(new Map(items.map((item) => [item.id, item])).values());
    const photos = unique(group.flatMap((entry) => entry.photos || []));
    const aiVersions = unique(group.flatMap((entry) => (entry.aiVersions || []).map(ai => ({ ...ai, originEntryId: entry.id }))));
    const aiGroups = unique(group.flatMap((entry) => (entry.aiGroups || []).map(g => ({ ...g, originEntryId: entry.id }))));
    return {
      ...primary,
      photos,
      aiVersions,
      aiGroups,
      teacherNote: ranked.find((entry) => entry.teacherNote)?.teacherNote || null,
      status: deriveEntryStatus({ photos, aiVersions }),
      createdAt: Math.min(...group.map((entry) => entry.createdAt)),
      updatedAt: Math.max(...group.map((entry) => entry.updatedAt)),
    };
  });
}

export function nextTeamColorKey(
  existing: Array<Pick<ClassroomTeam, 'colorKey' | 'archived'>>
): ClassroomTeamColorKey {
  const used = new Set(
    existing.filter((t) => !t.archived).map((t) => t.colorKey)
  );
  const available = CLASSROOM_TEAM_COLOR_ORDER.find((k) => !used.has(k));
  return available || CLASSROOM_TEAM_COLOR_ORDER[existing.length % CLASSROOM_TEAM_COLOR_ORDER.length];
}

/** True when an AI version is a real uploaded creation (has a poster or asset URL). */
export function isAttachedAiCreation(ai: PortfolioAiVersion): boolean {
  return Boolean(ai.posterUrl || ai.url);
}

export function isArchivedAiCreation(ai: PortfolioAiVersion): boolean {
  return Boolean(ai.archived);
}

export function attachedAiCreations(ais: PortfolioAiVersion[] | undefined): PortfolioAiVersion[] {
  return (ais || []).filter(isAttachedAiCreation);
}

/** Creations kids (and reveal) should see — attached and not archived. */
export function visibleAiCreations(ais: PortfolioAiVersion[] | undefined): PortfolioAiVersion[] {
  return attachedAiCreations(ais).filter((ai) => !isArchivedAiCreation(ai));
}

export function archivedAiCreations(ais: PortfolioAiVersion[] | undefined): PortfolioAiVersion[] {
  return attachedAiCreations(ais).filter(isArchivedAiCreation);
}

export type PortfolioRevealReadiness = 'hidden' | 'still_cooking' | 'ready_to_reveal' | 'revealed';

export function portfolioRevealReadiness(entry: PortfolioEntry | undefined): PortfolioRevealReadiness {
  if (!entry?.photos?.length) return 'hidden';
  if (!visibleAiCreations(entry.aiVersions).length) return 'still_cooking';
  return entry.status === 'revealed' ? 'revealed' : 'ready_to_reveal';
}

export function deriveEntryStatus(entry: Pick<PortfolioEntry, 'photos' | 'aiVersions'>): PortfolioEntryStatus {
  const photos = visiblePortfolioPhotos(entry.photos);
  const visible = visibleAiCreations(entry.aiVersions);
  if (!photos.length && !visible.length) return 'draft';
  // Grouped work is complete only when every option has been revealed.
  if (visible.some(a => a.groupId)) {
    if (visible.length && visible.every(a => a.status === 'revealed')) return 'revealed';
  } else if (visible.some((a) => a.status === 'revealed')) return 'revealed';
  if (visible.length) return 'ai_ready';
  if (photos.length) return 'has_photos';
  return 'draft';
}

export function summarizeEntriesForLevel(
  entries: PortfolioEntry[],
  levelId: string
): { photoCount: number; aiReady: number; aiRevealed: number; teamCountWithPhotos: number } {
  const forLevel = entries.filter((e) => e.levelId === levelId);
  let photoCount = 0;
  let aiReady = 0;
  let aiRevealed = 0;
  let teamCountWithPhotos = 0;
  for (const e of forLevel) {
    const n = visiblePortfolioPhotos(e.photos).length;
    photoCount += n;
    if (n > 0) teamCountWithPhotos += 1;
    const visible = visibleAiCreations(e.aiVersions);
    if (visible.some((a) => a.status === 'revealed')) aiRevealed += 1;
    else if (visible.length) aiReady += 1;
  }
  return { photoCount, aiReady, aiRevealed, teamCountWithPhotos };
}

export function teamSessionStatus(
  entries: PortfolioEntry[],
  teamId: string,
  levelId: string
): { photoCount: number; label: 'none' | 'photos' | 'ai_ready' | 'revealed' } {
  const entry = entries.find((e) => e.teamId === teamId && e.levelId === levelId);
  if (!entry || !visiblePortfolioPhotos(entry.photos).length) return { photoCount: 0, label: 'none' };
  const photoCount = visiblePortfolioPhotos(entry.photos).length;
  const visible = visibleAiCreations(entry.aiVersions);
  if (visible.some((a) => a.status === 'revealed')) {
    return { photoCount, label: 'revealed' };
  }
  if (visible.length) {
    return { photoCount, label: 'ai_ready' };
  }
  return { photoCount, label: 'photos' };
}
